import { ref, computed, watch } from 'vue';
import { router } from "@inertiajs/vue3";
import { defineStore, storeToRefs } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { formatarParaMenuLista } from "@/helpers/formatter";
import { formatarParaCarrossel } from "@/helpers/formatter";
import SolicitacaoAtendimentoService from "@/services/SolicitacaoAtendimentoService";

export const useServicosStore = defineStore('servicos', () => {
    const personasQueUtilizamNavegacaoPorInstituicoesFiltrada = ref([6]);

    const basePathComAgrupamento = computed(() => {
        return route('servicos', {
            agrupamento: agrupamento.value,
            path: null,
            pesquisa: null
        });
    });

    // CARREGAMENTO MENU
    const menuInstituicoes = ref([]);
    const menuPersonas = ref([]);
    const menusInstituicoesFiltroPorPersona = ref({});

    const ultimaVezCarregadoEm = ref(null);
    const tempoExpiracaoCache = 60 * 60 * 1000; // 1 hora

    // NAVEGAÇÃO ENTRE NÓS DO MENU
    const currentPathArray = ref([]);

    // FILTRO
    const agrupamento = ref('instituicoes');
    const termoBusca = ref('');

    const utilizaNavegacaoPorPersonasFiltrada = computed(() => {
        return agrupamento.value === 'personas' &&
               isAuthenticated.value &&
               personasQueUtilizamNavegacaoPorInstituicoesFiltrada.value.includes(Number(currentPathArray.value?.[0]));
    });

    const personaQueEstaUtilizandoNavegacaoPorInstituicoesFiltrada = computed(() => {
        if (!utilizaNavegacaoPorPersonasFiltrada.value) return null;

        return currentPathArray.value?.[0];
    });

    // AUTH STORE
    const authStore = useAuthStore();

    const { isAuthenticated } = storeToRefs(authStore);

    // LOADING
    const loading = ref(false);

    // ERRO
    const erro = ref(false);

    const currentMenuData = ref([]);

    const currentMenuNode = computed(() => findNodeByPath(currentPathArray.value));

    const baseNodeLabel = computed(() => agrupamento.value === 'personas' ? 'Personas' : 'Instituições');

    /**
     * Menu de Serviços no formato do componente Menu e MenuLista
     */
    const menuItensArray = computed(() => {
        if (!currentMenuNode.value) return [];

        return currentMenuNode.value.children.map(item => formatarItem(item));
    });

    /**
     * Menu de Serviços filtrado de acordo com o agrupamento atual (agrupamento) e filtro (termoBusca)
     */
    const menuItensArrayFiltrados = computed(() => {
        const data = currentMenuData.value;

        if (!data || data.length === 0) return [];

        if (!termoBusca.value.trim()) {
            return menuItensArray.value;
        }

        const todosItens = flattenMenuItems(data);

        const palavrasBusca = normalizar(termoBusca.value).split(/\s+/).filter(Boolean);

        const filtrados = todosItens.filter(item => {
            const descricao = item.descricao || item.nome || '';
            const descricaoNormalizada = normalizar(descricao);

            return palavrasBusca.every(palavra =>
                descricaoNormalizada.includes(palavra)
            );
        });

        return filtrados.map(item => formatarItem(item));
    });

    /**
     * Instituições no formato esperado pelo componente AppCarousel
     */
    const menuInstituicoesCarrossel = computed(() => {
        return menuInstituicoes.value.map(item => formatarParaCarrossel(
            item.nome,
            resolveItemIcon(item),
            () => router.visit(route('servicos', { agrupamento: 'instituicoes', path: item.id }))
        ));
    });

    /**
     * Personas no formato esperado pelo componente AppCarousel
     */
    const menuPersonasCarrossel = computed(() => {
        return menuPersonas.value.map(item => formatarParaCarrossel(
            item.nome,
            resolveItemIcon(item),
            () => router.visit(route('servicos', { agrupamento: 'personas', path: item.id }))
        ));
    });

    /**
     * Observa alterações na navegação para exibir o menu correto de acordo com o path
     */
    watch(
        [
            utilizaNavegacaoPorPersonasFiltrada,
            agrupamento,
            menuPersonas,
            menuInstituicoes,
            () => menusInstituicoesFiltroPorPersona
                .value[personaQueEstaUtilizandoNavegacaoPorInstituicoesFiltrada.value]
        ],
        () => {
            if (utilizaNavegacaoPorPersonasFiltrada.value) {
                currentMenuData.value = menusInstituicoesFiltroPorPersona
                    .value[personaQueEstaUtilizandoNavegacaoPorInstituicoesFiltrada.value] || [];
            } else {
                currentMenuData.value = agrupamento.value === 'personas' ? menuPersonas.value : menuInstituicoes.value;
            }
        },
        { immediate: true, flush: 'sync' }
    );

    function setAgrupamento(agpmto) {
        agrupamento.value = agpmto;
    }

    function setTermoBusca(termo) {
        termoBusca.value = termo;
    }

    function setCurrentPath(path = null) {
        currentPathArray.value = path;
    }

    const getMenu = async (force = false) => {
        const agora = Date.now();

        const tempoNaoExpirado = agora - ultimaVezCarregadoEm.value < tempoExpiracaoCache;

        let personaId = null;

        // Valida se o menu já foi carregado
        if (utilizaNavegacaoPorPersonasFiltrada.value && !force) {
            if (
                menusInstituicoesFiltroPorPersona
                    .value[personaQueEstaUtilizandoNavegacaoPorInstituicoesFiltrada.value] &&
                tempoNaoExpirado
            ) {
                return;
            }

            personaId = personaQueEstaUtilizandoNavegacaoPorInstituicoesFiltrada.value;
        } else if (
            menuInstituicoes.value.length > 0 &&
            menuPersonas.value.length > 0 &&
            tempoNaoExpirado
        ) {
            return;
        }

        erro.value = false;
        loading.value = true;

        try {
            const { data } = await SolicitacaoAtendimentoService.obterMenu({
                persona_id: personaId
            });

            if (personaId) {
                menusInstituicoesFiltroPorPersona.value[personaId] = data.menu_por_persona;
            } else {
                menuInstituicoes.value = data.menu;
                menuPersonas.value = data.menu_por_persona;
            }

            ultimaVezCarregadoEm.value = agora;
        } catch (e) {
            erro.value = true;

            console.error(e);
        } finally {
            loading.value = false;
        }
    }

    function flattenMenuItems(nodes, path = [], seenIds = new Set()) {
        let result = [];

        for (const node of nodes) {
            if (seenIds.has(String(node.id))) continue;

            seenIds.add(String(node.id));

            const currentPath = [...path, node.id];

            result.push({
                ...node,
                currentPath: currentPath,
                breadcrumb_string: resolveBreadcrumb(currentPath, false).map(u => u.label).join(" > ")
            });

            if (node.children && node.children.length > 0) {
                result = result.concat(flattenMenuItems(node.children, currentPath, seenIds));
            }
        }

        return result;
    }

    function findNodeByPath(path) {
        const data = currentMenuData.value;

        if (!data) return null;

        if (!path) {
            return {
                id: '',
                descricao: baseNodeLabel.value,
                children: data
            }
        }

        let currentLevel = data;
        let currentNode = null;

        for (const code of path) {
            currentNode = currentLevel.find(item =>
                String(item.id) === String(code)
            );

            if (!currentNode) return null;

            currentLevel = currentNode.children ?? [];
        }

        return currentNode;
    }

    function resolveBreadcrumb(path, withBaseBreadcrumb = true) {
        const data = currentMenuData.value;

        if (!data) return [];

        let currentLevel = data;
        let route = basePathComAgrupamento.value;

        const breadcrumb = [];

        if (withBaseBreadcrumb) {
            breadcrumb.push({
                label: baseNodeLabel.value,
                route: route
            });
        }

        for (const code of path || []) {
            const node = currentLevel.find(item =>
                String(item.id) === String(code)
            );

            if (!node) break;

            route += `/${node.id}`;

            breadcrumb.push({
                label: node.nome || node.descricao,
                route: route
            });

            currentLevel = node.children ?? [];
        }

        return breadcrumb;
    }

    function formatarItem(item) {
        const itemDesabilitado = !item.children && !temPermissaoParaAcessarFormulario(item);

        let itemRoute = resolveItemRoute(item);

        let itemIcon = resolveItemIcon(item, itemDesabilitado);

        return formatarParaMenuLista(
            item.id + '_' + (item.depto_id ?? ''),
            item.descricao || item.nome,
            itemIcon,
            itemRoute,
            item.children ? item.children.length : null,
            itemDesabilitado,
            'Acesso bloqueado. Acesse sua conta para habilitar o acesso.',
            item.breadcrumb_string
        );
    }

    function resolveItemRoute(item) {
        let itemRoute = '';

        if (item.rota) {
            itemRoute = '/' + item.rota;
        } else if (item.children) {
            itemRoute += basePathComAgrupamento.value;

            if (item.currentPath) {
                itemRoute += '/' + item.currentPath.join('/');
            } else {
                itemRoute += currentPathArray.value ? `/${currentPathArray.value.join('/')}` : '';
                itemRoute += `/${item.id}`;
            }
        } else {
            itemRoute += route('formulario', { formularioId: item.id });
        }

        return itemRoute;
    }

    function resolveItemIcon(item, itemDesabilitado = false) {
        if (item.estrutura && (item.estrutura === 'instituicao' || item.estrutura === 'persona')) {
            if (item.estrutura === 'instituicao') return 'fa-solid fa-landmark';

            if (item.estrutura === 'persona') {
                switch (item.nome.toUpperCase()) {
                    case "CIDADAO":
                        return 'fa-solid fa-user';
                    case "CONTRIBUINTE IMOBILIARIO":
                        return 'fa-solid fa-hand-holding-dollar';
                    case "ESCRITORIO CONTABIL":
                        return 'fa-solid fa-calculator';
                    case "PESSOA JURIDICA":
                        return 'fa-solid fa-shop';
                    case "PESSOA FISICA":
                        return 'fa-solid fa-user-circle';
                    case "SERVIDOR":
                        return 'fa-solid fa-id-card-clip';
                    case "CARTORIO EXTRA JUDICIAL":
                        return 'fa-solid fa-stamp';
                    case "FORNECEDOR":
                        return 'fa-solid fa-cart-shopping';
                    case "MOTORISTA":
                        return 'fa-solid fa-car';
                    case "GERAIS":
                        return 'fa-solid fa-globe';
                }
            }
        } else if (item.children) {
            return null;
        } else if (itemDesabilitado) {
            return 'fa-solid fa-lock';
        } else {
            return 'fa-solid fa-file-pen';
        }

        return '';
    }

    function temPermissaoParaAcessarFormulario(node) {
        return (node.identificado?.toUpperCase() === 'SIM' && isAuthenticated.value) ||
               node.identificado?.toUpperCase() === 'NÃO' ||
               node.identificado?.toUpperCase() === 'NAO';
    }

    function normalizar(texto) {
        return texto
            .normalize("NFD")                 // remove acentos
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^\w\s]/g, " ")         // remove pontuação
            .toUpperCase();
    }

    return {
        personasQueUtilizamNavegacaoPorInstituicoesFiltrada,
        personaQueEstaUtilizandoNavegacaoPorInstituicoesFiltrada,
        menuInstituicoes,
        menuInstituicoesCarrossel,
        menuPersonas,
        menuPersonasCarrossel,
        menusInstituicoesFiltroPorPersona,
        loading,
        erro,
        ultimaVezCarregadoEm,
        currentMenuNode,
        menuItensArray,
        menuItensArrayFiltrados,
        termoBusca,
        agrupamento,
        setAgrupamento,
        setTermoBusca,
        getMenu,
        findNodeByPath,
        resolveBreadcrumb,
        temPermissaoParaAcessarFormulario,
        setCurrentPath
    };
});
